import {BaseFlagProps, openDialog} from 'platform/components';
import {ThemeIconKey} from 'platform/foundation';

import {useLocation} from 'react-router-dom';

import {isNil, isNotNil, isEmpty} from 'ramda';

import {useGetParticipationQuery} from '@dms/api/core';
import {CustomerResponseBodyV2} from '@dms/api/customer';
import {BusinessCaseResponseBody} from '@dms/api/finance';
import {
  useGetBusinessCaseByBusinessCaseQuery,
  useGetLastActiveBuyingBusinessCaseQuery,
  useGetLastActiveSellingBusinessCaseQuery,
  useGetVehicleV2Query,
} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {useGetSaleVehicleQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {businessCaseRoutes, customerRoutes, testIds, vehiclesRoutes} from '@dms/routes';
import {
  EMPTY_PLACEHOLDER,
  SnippetProps,
  getBusinessCaseSnippet,
  getBusinessCaseStateFlag,
  getBusinessCaseTypeFlag,
  getNaturalPersonFullName,
  useAddress,
  useBusinessCaseActions,
  usePermissions,
} from '@dms/shared';

import {buildArray, buildObject, composePath, useRequiredParams} from 'shared/web';

import {useSelectVehicleContext} from '../../../hooks/useSelectVehicleContext';
import {getContactPersonData} from '../../../utils/getContactPersonData';
import {getPurchaseVehicleIdFromBusinessCase} from '../../../utils/getPurchaseVehicleIdFromBusinessCase';
import {getSaleVehicleIdFromBusinessCase} from '../../../utils/getSaleVehicleIdFromBusinessCase';
import {getSnippetVisibility} from '../../../utils/getSnippetVisibility';
import {ContractBillingInformationTitle} from '../components/ContractBillingInformationTitle';
import {SelectPurchaseForm} from '../components/SelectPurchaseForm';

export function useGetSnippets(
  businessCase: BusinessCaseResponseBody | undefined,
  customer: CustomerResponseBodyV2 | undefined
) {
  const {id: businessCaseId} = useRequiredParams();
  const contactPersonData = getContactPersonData(businessCase, customer);
  const {isActionEnabled} = useBusinessCaseActions(businessCaseId);
  const {composeShortAddress} = useAddress();

  const saleVehicleId = getSaleVehicleIdFromBusinessCase(businessCase);
  const purchaseVehicleId = getPurchaseVehicleIdFromBusinessCase(businessCase);
  const vehicleId = saleVehicleId ?? purchaseVehicleId;

  const location = useLocation();
  const hasOffer = Boolean((location?.state as {offerId?: string})?.offerId);

  const {data: businessCaseParticipation} = useGetParticipationQuery(
    {
      resourceId: EntityResourceIds.businessCase,
      recordId: businessCaseId,
    },
    {skip: isNil(businessCaseId)}
  );

  const {data: saleVehicle} = useGetSaleVehicleQuery(
    {vehicleId: vehicleId!},
    {skip: isNil(vehicleId)}
  );

  const isSaleVehicle = isNotNil(saleVehicle);

  const [
    canReadPurchase,
    canReadSell,
    hasCreateBusinessCasePurchasePermission,
    hasCreateBusinessCaseSellingPermission,
    hasAddVehicleToBuyPermission,
    hasAddVehicleToSellPermission,
    canPutVehicleUpForSale,
    canPutVehicleUpToBuy,
    canReadBusinessCase,
  ] = usePermissions({
    permissionKeys: [
      'viewBusinessCasePurchase',
      'viewBusinessCaseSelling',
      'createBusinessCasePurchase',
      'createBusinessCaseSelling',
      'addVehicleToBuy',
      'addVehicleToSell',
      'vehiclePutUpForSale',
      'vehiclePutUpToBuy',
      'businessCaseRead',
    ],
    scopes: {
      viewBusinessCasePurchase: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
      viewBusinessCaseSelling: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
    },
  });

  const canCreatePurchase = hasCreateBusinessCasePurchasePermission && hasAddVehicleToBuyPermission;
  const canCreateSell = hasCreateBusinessCaseSellingPermission && hasAddVehicleToSellPermission;
  const canCreateSaleVehicle = canPutVehicleUpForSale && canPutVehicleUpToBuy;

  const person =
    customer?.contacts.find((person) => businessCase?.contactPersonId === person.id) ||
    customer?.foundingPerson;

  const {data: sellingVehicle, isLoading: isLoadingSellingVehicle} = useGetVehicleV2Query(
    {vehicleId: saleVehicleId ?? ''},
    {skip: isNil(saleVehicleId)}
  );

  const {data: purchaseVehicle, isLoading: isLoadingPurchaseVehicle} = useGetVehicleV2Query(
    {vehicleId: purchaseVehicleId ?? ''},
    {skip: isNil(purchaseVehicleId)}
  );

  const {data: lastActiveBuyingBC, isError: isLastActiveBuyingBCError} =
    useGetLastActiveBuyingBusinessCaseQuery({vehicleId: vehicleId ?? ''}, {skip: isNil(vehicleId)});

  const {data: lastActiveSellingBC, isError: isLastActiveSellingBCError} =
    useGetLastActiveSellingBusinessCaseQuery(
      {vehicleId: vehicleId ?? ''},
      {skip: isNil(vehicleId)}
    );

  const {data: saleVehicleBC} = useGetBusinessCaseByBusinessCaseQuery(
    {businessCaseId: saleVehicle?.saleBusinessCaseId ?? ''},
    {skip: isNil(saleVehicle?.saleBusinessCaseId)}
  );

  const lastSellingBC = !isLastActiveSellingBCError ? lastActiveSellingBC : undefined;
  const sellingVehicleBC =
    ['contract', 'sold', 'handed-over'].includes(saleVehicle?.sellingState ?? '') &&
    isNotNil(saleVehicleBC)
      ? saleVehicleBC
      : lastSellingBC;

  const sellingBCWidget = getBusinessCaseSnippet(saleVehicle, sellingVehicleBC);
  const buyingBCWidget = getBusinessCaseSnippet(
    saleVehicle,
    !isLastActiveBuyingBCError ? lastActiveBuyingBC : undefined,
    !isEmpty(sellingBCWidget)
  );

  const snippetVisibility = getSnippetVisibility({
    businessCaseType: businessCase?.businessCaseType,
    vehicleType: saleVehicle?.type,
    sellingState: saleVehicle?.sellingState,
    permissions: {
      canReadSell,
      canReadPurchase,
      canReadBusinessCase,
      canCreatePurchase,
    },
    isSaleVehicle,
  });

  const {setSelectSaleVehicleOpen} = useSelectVehicleContext();

  const getBillingInformation = () => {
    const contractInformation = customer?.contractInformation.find(
      (contract) => contract.id === businessCase?.vehicleOrderContractInformationId
    );

    if (!contractInformation) {
      return null;
    }

    const isCompany =
      contractInformation.legalForm === 'LEGAL_ENTITY' ||
      contractInformation.legalForm === 'SELF_EMPLOYED';

    const address = isCompany
      ? composeShortAddress(contractInformation.businessInfo?.address)
      : composeShortAddress(contractInformation.person?.permanentAddress);

    const icon: ThemeIconKey = isCompany ? 'social/domain' : 'social/person';

    return {
      name: isCompany
        ? (contractInformation.businessInfo?.businessInfo?.tradeName ?? EMPTY_PLACEHOLDER)
        : getNaturalPersonFullName(contractInformation.person),
      regNumber:
        (isCompany
          ? contractInformation.businessInfo?.businessInfo?.registrationNumber
          : contractInformation.person?.personalIdentifier) ?? EMPTY_PLACEHOLDER,
      address: address ?? EMPTY_PLACEHOLDER,
      icon,
    };
  };

  const contractBillingInformation = getBillingInformation();

  const contractBillingInformationSnippet = buildObject<SnippetProps>()
    .label(i18n.t('entity.businessCase.labels.billingInformation'))
    .placeholder({
      title: i18n.t('general.labels.selectedNothing'),
    })
    .title(
      <ContractBillingInformationTitle
        name={contractBillingInformation?.name}
        icon={contractBillingInformation?.icon ?? 'social/person'}
        regNumber={contractBillingInformation?.regNumber}
      />,
      isNotNil(contractBillingInformation)
    )
    .parameters([contractBillingInformation?.address], isNotNil(contractBillingInformation))
    .build();

  const snippets = buildArray<SnippetProps>([
    {
      label: i18n.t('entity.businessCase.labels.contactPerson'),
      placeholder: {
        title: i18n.t(`entity.businessInfo.labels.noContactPerson`),
      },
      title: contactPersonData.name ?? EMPTY_PLACEHOLDER,
      parameters: contactPersonData.info,
      href: businessCase?.customerId
        ? composePath(customerRoutes.detail, {
            params: {id: businessCase?.customerId},
            isSearchParamsPreserved: false,
          })
        : undefined,
      'data-testid': testIds.businessCase.detail('contactPerson'),
      customerId: businessCase?.customerId,
      selectedContactId: person?.id,
      resourceId: EntityResourceIds.businessCase,
      recordId: businessCaseId,
      vehicleId,
    },
  ])
    .add(contractBillingInformationSnippet)
    .when(snippetVisibility.showSellingVehicle, () => ({
      label: i18n.t('page.businessCase.labels.selling'),
      placeholder: {
        title: i18n.t('entity.vehicle.actions.addVehicleToSell'),
        onClick: isActionEnabled('ADD_VEHICLE_TO_SELL')
          ? () => setSelectSaleVehicleOpen(true)
          : undefined,
        isDisabled: !canCreateSell || !isActionEnabled('ADD_VEHICLE_TO_SELL') || hasOffer,
      },
      title: isNotNil(saleVehicleId) ? sellingVehicle?.title : undefined,
      parameters: [
        (isNotNil(saleVehicleId) && sellingVehicle?.registrationPlate) ||
          i18n.t('entity.vehicle.notifications.noLicencePlate'),
        (isNotNil(saleVehicleId) && sellingVehicle?.vin) ||
          i18n.t('entity.vehicle.notifications.noVIN'),
      ],
      href:
        isNotNil(saleVehicleId) && isNotNil(sellingVehicle)
          ? composePath(vehiclesRoutes.detail, {
              params: {id: sellingVehicle?.id},
              isSearchParamsPreserved: false,
            })
          : undefined,
      isDisabled:
        (isNil(saleVehicleId) || !sellingVehicle) && !isActionEnabled('ADD_VEHICLE_TO_SELL'),
      'data-testid': testIds.businessCase.detail('saleVehicle'),
    }))
    .when(snippetVisibility.showSellingBc, () => ({
      label: i18n.t('page.businessCase.labels.selling'),
      placeholder: {
        title: i18n.t('entity.vehicle.actions.addVehicleToSell'),
        isDisabled: true,
      },
      'data-testid': testIds.businessCase.detail('sellingBC'),
      ...sellingBCWidget,
    }))
    .when(snippetVisibility.showBuyingVehicle, () => ({
      label: i18n.t('entity.businessCase.labels.buying'),
      placeholder: {
        isDisabled: !canCreateSaleVehicle || !isActionEnabled('ADD_VEHICLE_TO_BUY') || hasOffer,
        title: i18n.t('entity.vehicle.actions.addVehicleToBuy'),
        onClick: isActionEnabled('ADD_VEHICLE_TO_BUY')
          ? () =>
              openDialog(<SelectPurchaseForm businessCaseId={businessCaseId} />, {
                title: i18n.t('page.businessCase.labels.addPurchaseVehicle'),
                size: 'small',
              })
          : undefined,
      },
      title:
        isNotNil(purchaseVehicleId) && isNotNil(purchaseVehicle)
          ? purchaseVehicle.title
          : undefined,
      parameters: [
        (isNotNil(purchaseVehicleId) && purchaseVehicle?.registrationPlate) ||
          i18n.t('entity.vehicle.notifications.noLicencePlate'),
        (isNotNil(purchaseVehicleId) && purchaseVehicle?.vin) ||
          i18n.t('entity.vehicle.notifications.noVIN'),
      ],
      href:
        isNotNil(purchaseVehicleId) && purchaseVehicle
          ? composePath(vehiclesRoutes.detail, {
              params: {id: purchaseVehicle?.id},
              isSearchParamsPreserved: false,
            })
          : undefined,
      isDisabled:
        (isNil(purchaseVehicleId) || !purchaseVehicle) && !isActionEnabled('ADD_VEHICLE_TO_BUY'),
      'data-testid': testIds.businessCase.detail('purchaseVehicle'),
    }))
    .when(snippetVisibility.showBuyingBc, () => ({
      label: i18n.t('entity.businessCase.labels.buying'),
      placeholder: {
        title: i18n.t('entity.vehicle.actions.addVehicleToBuy'),
        isDisabled: true,
      },
      'data-testid': testIds.businessCase.detail('buyingBC'),
      ...buyingBCWidget,
    }))
    .when(businessCase?.brokerageBusinessCase && canReadPurchase, () => ({
      label: i18n.t('page.businessCase.labels.brokerageBusinessCase'),
      icon: 'places/business_center',
      title: businessCase?.brokerageBusinessCase?.customerName ?? EMPTY_PLACEHOLDER,
      flags: buildArray<BaseFlagProps>()
        .when(
          businessCase?.brokerageBusinessCase?.businessCaseType,
          getBusinessCaseTypeFlag(
            businessCase?.brokerageBusinessCase?.businessCaseType
          ) as BaseFlagProps
        )
        .when(
          businessCase?.brokerageBusinessCase?.businessCaseState,
          getBusinessCaseStateFlag(
            businessCase?.brokerageBusinessCase?.businessCaseState
          ) as BaseFlagProps
        ),
      parameters: [
        businessCase?.brokerageBusinessCase?.code,
        `${i18n.t('entity.vehicle.labels.seller')}: ${
          businessCase?.brokerageBusinessCase?.createdByFirstName ?? EMPTY_PLACEHOLDER
        } ${businessCase?.brokerageBusinessCase?.createdByLastName ?? EMPTY_PLACEHOLDER}`,
      ],
      href: businessCase?.brokerageBusinessCase?.id
        ? composePath(businessCaseRoutes.overview, {
            params: {id: businessCase?.brokerageBusinessCase?.id},
            isSearchParamsPreserved: false,
          })
        : undefined,
      'data-testid': testIds.businessCase.detail('brokerageBusinessCase'),
    }));

  return {snippets, isLoading: isLoadingPurchaseVehicle || isLoadingSellingVehicle};
}
